import { createContext, useContext, ReactNode, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth0 } from "@auth0/auth0-react";
import * as authTypes from "../constants/authTypes";
import { useNavigate } from "react-router";
import { useSessionStorage } from "../utils/useSessionStorage";

interface User {
  name?: string;
  email?: string;
  id?: string;
}

interface AuthContextType {
  user: User | null;
  logout: () => void;
  login: () => void;
  setUser: (user: User) => void;
  getToken: () => Promise<string | null>;
  isAuthenticated: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
  authType: "B2B" | "B2C" | "AUTH0" | "BASIC" | "ANONYMOUS";
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const MsalAuthProvider = ({ children }: { children: ReactNode }) => {
  const { accounts, instance, inProgress } = useMsal();

  const user: User | null = accounts[0]
    ? {
        name: accounts[0].name,
        email: accounts[0].username,
        id: accounts[0].localAccountId,
      }
    : null;

  const getLoginRequestScope = () => [
    process.env.REACT_APP_AUTH === authTypes.B2C
      ? `https://${process.env.REACT_APP_DOMAIN}/${process.env.REACT_APP_SCOPE_PREFIX}/${process.env.REACT_APP_SCOPE}`
      : `${process.env.REACT_APP_B2B_SCOPE}`,
  ];

  const loginRequest = {
    scopes: getLoginRequestScope(),
  };

  const login = () => instance.loginRedirect(loginRequest);
  const logout = () => instance.logoutRedirect();
  const setUser = (user: User) => {};
  const isAuthenticated = accounts.length > 0;

  const getToken = async (): Promise<string | null> => {
    if (accounts.length === 0 && inProgress === "none") {
      await instance.loginRedirect(loginRequest);
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.warn("Error getting token", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, logout, login, setUser, isAuthenticated, getToken }}>
      {children}
    </AuthContext.Provider>
  );
};

const Auth0AuthProvider = ({ children }: { children: ReactNode }) => {
  const auth0 = useAuth0();

  const user: User | null = auth0.user
    ? {
        name: auth0.user.name,
        email: auth0.user.email,
        id: auth0.user.sub,
      }
    : null;

  const login = () => auth0.loginWithRedirect();
  const logout = () => auth0.logout({ logoutParams: { returnTo: window.location.origin } });
  const setUser = (user: User) => {};
  const isAuthenticated = auth0.isAuthenticated;

  const getToken = async (): Promise<string | null> => {
    try {
      return await auth0.getAccessTokenSilently();
    } catch (error) {
      console.error("Error getting token:", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, logout, login, setUser, isAuthenticated, getToken }}>
      {children}
    </AuthContext.Provider>
  );
};

const BasicAuthProvider = ({ children }: { children: ReactNode }) => {
  let navigate = useNavigate();

  const [basicUser, setBasicUser] = useSessionStorage("user", null);
  const user: User = basicUser;

  const login = () => {};
  const logout = () => {
    setBasicUser(null);
    sessionStorage.setItem("token", "");
    navigate("/", { replace: true });
  };
  const setUser = (user: User) => {
    setBasicUser(user);
  };
  const isAuthenticated = user !== null;

  const getToken = async (): Promise<string | null> => {
    try {
      return sessionStorage.getItem("token");
    } catch (error) {
      console.error("Error getting token:", error);
      return null;
    }
  };

  const value = useMemo(
    () => ({
      user,
      logout,
      login,
      setUser,
      isAuthenticated,
      getToken,
    }),
    [user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const AnonymousAuthProvider = ({ children }: { children: ReactNode }) => {
  const user: User = { name: "", email: "" };

  const login = () => {};
  const logout = () => {};
  const setUser = (user: User) => {};
  const isAuthenticated = true;
  const getToken = async (): Promise<string | null> => {
    return null;
  };

  const value = useMemo(
    () => ({
      user,
      logout,
      login,
      setUser,
      isAuthenticated,
      getToken,
    }),
    [user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider = ({ children, authType }: AuthProviderProps) => {
  if (authType === authTypes.AUTH0) {
    return <Auth0AuthProvider>{children}</Auth0AuthProvider>;
  } else if (authType === authTypes.BASIC) {
    return <BasicAuthProvider>{children}</BasicAuthProvider>;
  } else if (authType === authTypes.ANONYMOUS) {
    return <AnonymousAuthProvider>{children}</AnonymousAuthProvider>;
  } else {
    return <MsalAuthProvider>{children}</MsalAuthProvider>;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
